// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { Gender, MaritalStatus } from './client';

/**
 * Override DTO returned by the API.
 * Represents an active detail override for a policy signatory.
 */
export interface PolicySignatoryOverrideDTO {
  id: string;
  policySignatoryId: string;
  overrideFirstName: string | null;
  overrideLastName: string | null;
  overrideEmail: string | null;
  overrideAddressLine1: string | null;
  overrideAddressLine2: string | null;
  overrideAddressLine3: string | null;
  overrideTownCity: string | null;
  overrideStateProvinceCounty: string | null;
  overridePostalCode: string | null;
  overrideCountryCode: string | null;
  overrideCountry: string | null;
  overrideGender: Gender | null;
  overrideMaritalStatus: MaritalStatus | null;
  reason: string;
  overriddenFields: string[];
  createdBy: string;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string | null;
}

/**
 * Request DTO for creating or updating an override.
 * All override fields are nullable (null = use profile value).
 * At least one override field must be non-null.
 * Reason is required (5-1000 characters).
 */
export interface PolicySignatoryOverrideCreateDTO {
  overrideFirstName: string | null;
  overrideLastName: string | null;
  overrideEmail: string | null;
  overrideAddressLine1: string | null;
  overrideAddressLine2: string | null;
  overrideAddressLine3: string | null;
  overrideTownCity: string | null;
  overrideStateProvinceCounty: string | null;
  overridePostalCode: string | null;
  overrideCountryCode: string | null;
  overrideCountry: string | null;
  overrideGender: Gender | null;
  overrideMaritalStatus: MaritalStatus | null;
  reason: string;
}

/**
 * Resolved (merged) client details for display.
 * Each field contains the effective value: override value if present,
 * otherwise client profile value.
 */
export interface ResolvedClientDetailsDTO {
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  addressLine1: string | null;
  addressLine2: string | null;
  addressLine3: string | null;
  townCity: string | null;
  stateProvinceCounty: string | null;
  postalCode: string | null;
  countryCode: string | null;
  country: string | null;
  hasOverride: boolean;
  overriddenFields: string[];
}

/**
 * Summary of a single override in a profile (used in the profile overrides list).
 */
export interface OverrideSummaryItem {
  id: string;
  policySignatoryId: string;
  policyId: string;
  policyNumber: string;
  providerName: string;
  clientProfileId: string;
  clientName: string;
  overriddenFields: string[];
  reason: string;
  createdBy: string;
  createdAt: string;
}

/**
 * Profile overrides summary response.
 */
export interface ProfileOverrideSummaryDTO {
  profileId: string;
  overrides: OverrideSummaryItem[];
  totalCount: number;
}

/**
 * Field label mapping for display purposes.
 */
export const OVERRIDE_FIELD_LABELS: Record<string, string> = {
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Email',
  addressLine1: 'Address Line 1',
  addressLine2: 'Address Line 2',
  addressLine3: 'Address Line 3',
  townCity: 'Town/City',
  stateProvinceCounty: 'State/Province',
  postalCode: 'Postal Code',
  countryCode: 'Country Code',
  country: 'Country',
  gender: 'Gender',
  maritalStatus: 'Marital Status',
};
